import {useEffect, useMemo, useRef, useState} from 'react'
import {css} from '@emotion/react'
import {Tree as AntdTree} from 'antd'
import _Tree from '@/script/Tree.mjs'
import {fuzzyMatch} from './match.mjs'
import {SearchList} from './SearchList.jsx'

const cssTree = css({
    //fontSize: 12,
    overflow: 'auto',

    '.ant-tree-indent-unit': {
        width: 16,
    },

    '.ant-tree-switcher': {
        width: 12,
    },

    '.ant-tree-list-holder-inner': {
        display: 'grid !important',
    },

    '.ant-tree-treenode': {
        padding: '2px 8px',

        '&:hover': {
            backgroundColor: '#f5f5f5',
        },

        '&:has(.ant-tree-node-selected)': {
            backgroundColor: '#0099CC',
        },
    },

    '.ant-tree-node-content-wrapper': {
        display: 'inline-flex',
        backgroundColor: 'transparent !important',
    },

    '.ant-tree-node-selected': {
        color: '#fff',
    },

    '.ant-tree-title': {
        display: 'inline-block',
        maxWidth: '16em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
})

export const Tree = ({
    defaultExpandAll,
    defaultSelectedKey,
    data,
    match = fuzzyMatch,
    searchText,
    searchListItem,
    onMatch,
    onSelect,
    ...props
}) => {
    const [selectedKey, setSelectedKey] = useState(defaultSelectedKey)
    const refEl = useRef()
    const tree = new _Tree({children: data})

    const expandedKeysFromKey = function* (key) {
        const node = tree.getNode(key)

        for (const n of tree.chain(node.parent)) {
            yield n.id
        }
    }

    const [expandedKeys, setExpandedKeys] = useState(() => {
        if (defaultExpandAll) {
            return [...
                tree
                    .dfs(tree.root.children)
                    .filter(n => n.firstChild)
                    .map(n => n.id)
            ]
        }
        else if (defaultSelectedKey) {
            return [...expandedKeysFromKey(defaultSelectedKey)]
        }
        else {
            return []
        }
    })

    const matches = useMemo(
        () => {
            if (! searchText) {
                return []
            }

            const nodes = [...tree.dfs(tree.root.children)]

            const items = nodes.map(n => {
                const {title, titleText} = n.data
                return [n.id, titleText ?? title]
            })

            return match(searchText, items)
        },

        [data, searchText]
    )

    useEffect(
        () => {
            if (! onMatch) {
                return
            }

            const data = matches.map(([key]) => {
                const {data} = tree.getNode(key)
                return data
            })

            onMatch(data)
        },

        [matches]
    )

    useEffect(
        () => {
            if (searchText || ! selectedKey) {
                return
            }

            const el = refEl.current.querySelector('.ant-tree-node-selected')
            el?.scrollIntoViewIfNeeded()
        },

        [searchText, selectedKey]
    )

    const {children} = tree.exportTree(
        tree.root,
        ({id, data: {icon, title}}) => ({key: id, icon, title})
    )

    const _onSelect = key => {
        setSelectedKey(key)
        const {data} = tree.getNode(key)
        onSelect(data)

        setExpandedKeys(keys => {
            const ks = new Set(keys)

            for (const k of expandedKeysFromKey(key)) {
                ks.add(k)
            }

            return [...ks]
        })
    }

    if (searchText) {
        return (
            <SearchList
                matches={matches}
                listItem={searchListItem}
                tree={tree}
                onSelect={_onSelect}
                {...props}
            />
        )
    }
    else {
        return (
            <div
                ref={refEl}
                style={{display: 'contents'}}
            >
                <AntdTree
                    css={cssTree}
                    ref={refEl}
                    blockNode
                    expandedKeys={expandedKeys}
                    selectedKeys={selectedKey ? [selectedKey] : []}
                    showIcon
                    treeData={children}
                    onSelect={([key]) => key && _onSelect(key)}
                    onExpand={setExpandedKeys}
                    {...props}
                />
            </div>
        )
    }
}
